import { appCode } from 'config';
import { ROUTES } from '@/const/routes';
import { CONVERSATION_ASSIST_ROUTES } from '@conversationAssist/const/conversationAssist';
import hasConversationAssistPermission from '@/common/permission';
import { setRouteParams } from '@/store/mutations';
import { getActiveRouteParams } from '@/store/getters';

const ConversationAssist = () => import('@/components/conversation-assist/ConversationAssist');
const ConversationAssistHome = () => import('@/components/conversation-assist/Home/Home');
const ConversationAssistWelcome = () => import('@/components/conversation-assist/Welcome/Welcome');
const ConversationAssistSettings = () => import('@/components/conversation-assist/Settings/Settings');
const ConversationAssistRecommendationSources = () => import('@/components/conversation-assist/RecommendationSources/RecommendationSources');
const ConversationAssistKnowledgeBases = () => import('@/components/conversation-assist/RecommendationSources/components/KnowledgeBases');
const ConversationAssistBots = () => import('@/components/conversation-assist/RecommendationSources/components/Bots');
const ConversationAssistReports = () => import('@/components/conversation-assist/Reports/Reports');
const ConversationAssistRecommendationRules = () => import('@/components/conversation-assist/RecommendationRules/RecommendationRules');
const ConversationAssistRules = () => import('@/components/conversation-assist/RecommendationRules/components/Rules');
const ConversationAssistKBRules = () => import('@/components/conversation-assist/RecommendationRules/components/KnowledgeBases');
const ConversationAssistBotRules = () => import('@/components/conversation-assist/RecommendationRules/components/Bots');
const ConversationAssistCreateKnowledgeBaseRule = () => import(
  '@/components/conversation-assist/RecommendationRules/components/CreateKnowledgeBaseRule'
);
const ConversationAssistCreateBotRule = () => import('@/components/conversation-assist/RecommendationRules/components/CreateBotRule');
const ConversationAssistBotRuleDetails = () => import('@/components/conversation-assist/RecommendationRules/components/BotRuleDetails');
const ConversationAssistKbRuleDetails = () => import('@/components/conversation-assist/RecommendationRules/components/KbRuleDetails');
const ConversationAssistEditBotRule = () => import('@/components/conversation-assist/RecommendationRules/components/EditBotRule');
const ConversationAssistEditKbRule = () => import('@/components/conversation-assist/RecommendationRules/components/EditKbRule');

const meta = {
  translationKey: 'LEUIComponents.reporting.context.automation',
  translationSubKey: 'LEUIComponents.reporting.context.automation.conversationassist',
};

export default {
  path: `/${appCode}/${ROUTES.CONVERSATION_ASSIST}`,
  name: ROUTES.CONVERSATION_ASSIST,
  components: { [appCode]: ConversationAssist },
  meta,
  beforeEnter: (to, from, next) => {
    if (hasConversationAssistPermission()) {
      const { routeTo } = getActiveRouteParams() || {};
      if (!from.name && !routeTo) {
        setRouteParams({ routeTo: to.name });
        next({ name: ROUTES.CONVERSATION_ASSIST });
      } else {
        next();
      }
    } else {
      next({ name: ROUTES.LANDING });
    }
  },
  children: [
    {
      name: CONVERSATION_ASSIST_ROUTES.HOME,
      path: 'home',
      component: ConversationAssistHome,
      meta,
    },
    {
      name: CONVERSATION_ASSIST_ROUTES.WELCOME,
      path: 'welcome',
      component: ConversationAssistWelcome,
      meta,
    },
    {
      name: CONVERSATION_ASSIST_ROUTES.SETTINGS,
      path: 'settings',
      component: ConversationAssistSettings,
      meta,
    },
    {
      name: CONVERSATION_ASSIST_ROUTES.RECOMMENDATION_SOURCES,
      path: 'recommendation',
      component: ConversationAssistRecommendationSources,
      redirect: { name: CONVERSATION_ASSIST_ROUTES.KNOWLEDGE_BASES },
      meta,
      children: [
        {
          name: CONVERSATION_ASSIST_ROUTES.KNOWLEDGE_BASES,
          path: 'kb',
          component: ConversationAssistKnowledgeBases,
          meta,
        },
        {
          name: CONVERSATION_ASSIST_ROUTES.BOTS,
          path: 'bots',
          component: ConversationAssistBots,
          meta,
        },
      ],
    },
    {
      name: CONVERSATION_ASSIST_ROUTES.RECOMMENDATION_RULES,
      path: 'recommendationRules',
      component: ConversationAssistRecommendationRules,
      redirect: { name: CONVERSATION_ASSIST_ROUTES.RULES },
      meta,
      children: [
        {
          name: CONVERSATION_ASSIST_ROUTES.RULES,
          path: 'rules',
          component: ConversationAssistRules,
          redirect: { name: CONVERSATION_ASSIST_ROUTES.KB_RULES },
          meta,
          children: [
            {
              name: CONVERSATION_ASSIST_ROUTES.KB_RULES,
              path: 'kbRules',
              component: ConversationAssistKBRules,
              meta,
            },
            {
              name: CONVERSATION_ASSIST_ROUTES.BOTS_RULES,
              path: 'botRules',
              component: ConversationAssistBotRules,
              meta,
            },
          ],
        },
        {
          name: CONVERSATION_ASSIST_ROUTES.CREATE_KB_RULE,
          path: 'createKbRule',
          component: ConversationAssistCreateKnowledgeBaseRule,
          meta,
        },
        {
          name: CONVERSATION_ASSIST_ROUTES.CREATE_BOT_RULE,
          path: 'createBotRule',
          component: ConversationAssistCreateBotRule,
          meta,
        },
        {
          name: CONVERSATION_ASSIST_ROUTES.EDIT_KB_RULE,
          path: 'editKbRule/:id',
          component: ConversationAssistEditKbRule,
          meta,
        },
        {
          name: CONVERSATION_ASSIST_ROUTES.EDIT_BOT_RULE,
          path: 'editBotRule/:id',
          component: ConversationAssistEditBotRule,
          meta,
        },
        {
          name: CONVERSATION_ASSIST_ROUTES.KB_RULE_DETAILS,
          path: 'kbRuleDetails/:id',
          component: ConversationAssistKbRuleDetails,
          meta,
        },
        {
          name: CONVERSATION_ASSIST_ROUTES.BOT_RULE_DETAILS,
          path: 'botRuleDetails/:id',
          component: ConversationAssistBotRuleDetails,
          meta,
        },
      ],
    },
    {
      name: CONVERSATION_ASSIST_ROUTES.DETAILED_REPORTS,
      path: 'reports',
      component: ConversationAssistReports,
      meta,
    },
    {
      path: '*', redirect: { name: ROUTES.CONVERSATION_ASSIST },
    },
  ],
};
